import { WizMathUtils, WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { WizIcon, WizPopUp } from '@wizeoo/akm-component-lib';
import { BusinessException, EMPackEntry, EntityModificationPack, IncExcKey, VisionKey } from '@wizeoo/akm-message-lib';
import { CommitCmd } from '../../command/em/commit.cmd';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { IncExcKeyVO } from '../../vo/core/incexckey.vo';
import { IncExcContentObj } from '../../vo/core/tool/incex.content.obj';
import { IncExcMainObj } from '../../vo/core/tool/incex.main.obj';
import { EMEngine } from '../entities/entity-modification.engine';
import { EM_ACTION, VisionVO } from '@wizeoo/akm-compute-lib';
import { cloneDeep } from 'lodash-es';
/**
 * gestion des includes / excludes
 */
export class IncExcKeyEngine {

  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }

  public currentKey: IncExcKeyVO = null; // la clé en cours d'édition
  public currentClone: IncExcKeyVO = null; // le clone de travail

  /** alimentation du clone de travail sur la base du currentEdited */
  public clone(vo: IncExcKeyVO): IncExcKeyVO {
    let result: IncExcKeyVO = null;
    if (vo) {
      result = new IncExcKeyVO(this.model, cloneDeep(vo.source));
      const voidMe = result.keyMap; // juste un hit pour initialiser la map et la collection
    } else {
      result = null;
    }
    return result;
  }

  /* #############################################################
                    alimentation du modèle
     ############################################################# */

  /**
   * construction de la map des clés par BU et par vision et alimentation du modèle
   * @param coll la collection encapsulée, sur retour de find
   * @returns la map pour alimenter le modèle
   */
  public buildMap(coll: IncExcKeyVO[]): Map<string, Map<string, Map<string, IncExcKeyVO>>> {
    const result: Map<string, Map<string, Map<string, IncExcKeyVO>>> = new Map<
      string,
      Map<string, Map<string, IncExcKeyVO>>
    >();

    coll.forEach((iek: IncExcKeyVO) => {
      if (!result.has(iek.businessUnitCode)) {
        result.set(iek.businessUnitCode, new Map<string, Map<string, IncExcKeyVO>>());
      }
      if (!result.get(iek.businessUnitCode).has(iek.visionCode)) {
        result.get(iek.businessUnitCode).set(iek.visionCode, new Map<string, IncExcKeyVO>());
      }
      result.get(iek.businessUnitCode).get(iek.visionCode).set(iek.uniqueKey, iek); // la clé unique, visionCode|key|inc
    });

    this.model.incExcKeyDic = result;
    return result;
  }

  /** définition des précurseurs disponibles pour création des clés, selon la vision */
  public buildPrecursors(): IncExcMainObj[] {
    // ## définition du pool de données des clés éligibles selon la vision (ou pas, dans ce cas, tous les FD et Chronos de la BU)
    let precursors: IncExcMainObj[] = [];

    const currentVision: VisionVO = this.model.currentVision;
    if (!!currentVision) {
      // une vision, on prends les FD et chrono qui correspondent à la vision
      const keyMap: Map<string, string> = new Map(
        Object.values(currentVision.visionKeys).map((item: VisionKey) => [
          item.code.split(ViewData.CONTEXT_SEPARATOR)[0],
          item.code,
        ])
      );
      keyMap.set(currentVision.chronoCode, currentVision.chronoCode);

      precursors = Array.from(this.keyPartsMap.values());
      // console.log('precursor 1', precursors);
      precursors = precursors.filter((iemo: IncExcMainObj) => keyMap.has(iemo.code));
      // console.log('precursor 2', precursors);
    } else {
      precursors = Array.from(this.keyPartsMap.values());
    }
    return precursors;
  }

  /** définition du pool de lignes éligibles (BU & vision) et le retourne */
  public buildPool(): IncExcKeyVO[] {
    let incExcKeyCollection: IncExcKeyVO[] = [];
    if (this.model.incExcKeyDic?.has(this.model.businessUnitCode)) {
      if (this.model.incExcKeyDic.get(this.model.businessUnitCode).has(ViewData.wildcardDomain)) {
        incExcKeyCollection = Array.from(
          this.model
            .incExcKeyDic.get(this.model.businessUnitCode)
            .get(ViewData.wildcardDomain)
            .values()
        ); // collec de include/exc
      }

      if (!!this.model.visionCode) {
        if (
          this.model.incExcKeyDic.get(this.model.businessUnitCode).has(this.model.visionCode)
        ) {
          incExcKeyCollection = incExcKeyCollection.concat(
            Array.from(
              this.model
                .incExcKeyDic.get(this.model.businessUnitCode)
                .get(this.model.visionCode)
                .values()
            )
          );
        }
      }
    }

    this.model.incExcKeyCollection = incExcKeyCollection;
    return incExcKeyCollection;
  }

  private _keyPartsMap: Map<string, IncExcMainObj> = null; // la map de FD / Chrono encapsulés en IncExcMainObj code = IncExcMainObj. Les chronos sont préfixés CHRONO#
  /** accesseur sur les précurseurs de clés */
  public get keyPartsMap(): Map<string, IncExcMainObj> {
    if (!this._keyPartsMap) {
      this.generateKeyPartsMap();
    }
    return this._keyPartsMap;
  }

  /** construction des éléments de clé disponibles */
  public generateKeyPartsMap(): void {
    // alimentation de la DS principale
    let allThem: IncExcMainObj[] = this.model.freedataEngine.getFreeDataList().map((fd) => new IncExcMainObj(null, fd));
    allThem = allThem.concat(this.model.chronocodeTreeList.map((chro) => new IncExcMainObj(chro)));

    this._keyPartsMap = new Map(allThem.map((iemo) => [iemo.code, iemo])); // uniqueCode pour préfixe du Chrono
  }

  /**
   * pour une clé de incexcKeyVO, recherche de son objet IncExcContentObj correspondant
   * @param key la clé "parent" soit CHRONO@codechrono#level, soit codeFD#level
   * @param value la valeur du FD / chrono
   * @returns IncExcContentObj
   */
  public getKeyPart(key: string, value: string): IncExcContentObj {
    let result: IncExcContentObj = null;
    const parentkey: string = key.split(ViewData.CONTEXT_SEPARATOR)[0]; // la clé contient le level
    if (!!parentkey) {
      const iemo: IncExcMainObj = this.keyPartsMap.get(parentkey);
      if (!!iemo) {
        result = iemo.contentsMap.get(value);
      } else {
        console.log('inckey missing', key, value);
      }
    } else {
      console.log('inckey mismatch', key, value);
    }

    return result;
  }

  /* #############################################################
            persistence et synchro du modèle
            On fonctionne sur delete insert en cas d'update
     ############################################################# */

  /**
   * retourne la clé existante pour ce code unique dans le pool de données
   * @param uniqueKey clé d'unicité vision | key | inc
   * @returns la clé correspondante, si existe
   */
  public getIncExcKey(uniqueKey: string): IncExcKeyVO {
    let result: IncExcKeyVO = null;
    const existingKeys: Map<string, IncExcKeyVO> = new Map(
      this.model.incExcKeyCollection.map((item) => [item.uniqueKey, item])
    );
    result = existingKeys.get(uniqueKey);
    return result;
  }

  /**
   * create d'une clé
   * @param to la clé clone en cours de création
   * @param from la clé existante
   * @param callback retour dans la vue
   */
  public save(to: IncExcKeyVO, from: IncExcKeyVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      if (to.uniqueKey !== from.uniqueKey) {
        // et modification de la structure de clé
        to.id = null; // on perd l'id sur le clone, pour faire un create
        to.from = from; // on mémorise la source pour la supprimer au retour
        to.uid = WizMathUtils.createUUID(); // on génère aussi un nouvel UUID
        pack = EMEngine.addCreationEM(to, null); // insert
        pack = EMEngine.addRemoveEM(from, pack); // delete
      } else {
        // vrai update (le label)
        to.from = from; // on mémorise la source pour mettre à jour la véritable entité (on passe le TO au service)
        pack = EMEngine.addMergeEM(to, from, null); // update
      }
    } else {
      pack = EMEngine.addCreationEM(to, null);
    }

    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * Suppression d'une clé
   * @param key à supprimer
   * @param callback Callback pour le retour de service
   */
  public remove(key: IncExcKeyVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.addRemoveEM(key, null);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [key], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: IncExcKeyVO = cmd.sourceVO[0] as IncExcKeyVO;
      // une seule modification à la fois, packEntry donne l'objet
      let source: IncExcKey;
      // l'action (un seul type)
      let operation: string;

      // en cas de CREATE, c'est peut-être un deleteInsert
      const nbOps: number = cmd.result.entries.length;
      if (nbOps === 1) {
        source = cmd.result.entries[0].entity as IncExcKey;
        operation = cmd.result.ems[0].operation;
      } else {
        // on cherche le create
        cmd.result.entries.forEach((packEn: EMPackEntry) => {
          // console.log('entries', packEn);
          if (packEn.operation === EM_ACTION.CREATE) {
            source = packEn.entity as IncExcKey;
            operation = packEn.operation;
          }
        });
      }

      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.from.source = source; // on met à jour le vrai
            break;
          case EM_ACTION.CREATE:
            vo.source = source;
            this.addKey(vo);
            if (!!vo.from) {
              // c'est un deleteInsert
              this.deleteKey(vo.from);
            }
            break;
          case EM_ACTION.DELETE:
            this.deleteKey(vo);
            break;
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }

  /** ajout sur retour de service */
  private addKey(iek: IncExcKeyVO): void {
    const dic = this.model.incExcKeyDic;
    if (!dic) {
      // 1er init, pas de retour du service
      this.buildMap([iek]);
    } else {
      if (!dic.has(iek.businessUnitCode)) {
        dic.set(iek.businessUnitCode, new Map<string, Map<string, IncExcKeyVO>>());
      }
      if (!dic.get(iek.businessUnitCode).has(iek.visionCode)) {
        dic.get(iek.businessUnitCode).set(iek.visionCode, new Map<string, IncExcKeyVO>());
      }
      dic.get(iek.businessUnitCode).get(iek.visionCode).set(iek.key, iek);
      this.model.incExcKeyCollection.push(iek);
    }
  }

  /** delete sur retour de service */
  private deleteKey(iek: IncExcKeyVO): void {
    const dic = this.model.incExcKeyDic;
    dic.get(iek.businessUnitCode).get(iek.visionCode).delete(iek.key);
    this.model.incExcKeyCollection.splice(this.model.incExcKeyCollection.indexOf(iek), 1);
  }
}
